import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Compass } from "lucide-react";
import { site } from "../content/site";

const lineVariant = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeVariant = {
  hidden: { opacity: 0, y: 18 },
  show: (delay) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const frameRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: frameRef,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="home" data-testid="hero-section" className="relative pt-40 sm:pt-44 pb-16 overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(15,23,42,0.045) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.045) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage: "radial-gradient(ellipse 90% 70% at 50% 0%, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse 90% 70% at 50% 0%, black 30%, transparent 75%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.p
          custom={0}
          variants={fadeVariant}
          initial="hidden"
          animate="show"
          data-testid="hero-eyebrow"
          className="font-mono text-xs uppercase tracking-[0.2em] text-teal-700 bg-teal-50 border border-teal-200/60 px-3 py-1.5 rounded-full inline-block"
        >
          {site.hero.eyebrow}
        </motion.p>

        <h1 className="mt-7 font-display font-extrabold tracking-tight text-slate-900 text-[clamp(2.6rem,7.5vw,5.9rem)] leading-[1.03]">
          {site.hero.lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1 -mb-1">
              <motion.span
                className={`block ${i === 1 ? "text-teal-600" : ""}`}
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="show"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          custom={0.65}
          variants={fadeVariant}
          initial="hidden"
          animate="show"
          data-testid="hero-subheadline"
          className="mt-7 max-w-2xl text-lg sm:text-xl text-slate-600 leading-relaxed"
        >
          {site.hero.sub}
        </motion.p>

        <motion.div
          custom={0.8}
          variants={fadeVariant}
          initial="hidden"
          animate="show"
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            href={site.hero.primaryCta.href}
            data-testid="hero-cta-explore"
            className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-7 py-3.5 rounded-full shadow-sm shadow-teal-600/25 transition-all duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
          >
            {site.hero.primaryCta.label}
            <ArrowRight size={17} strokeWidth={2.5} />
          </a>
          <a
            href={site.hero.secondaryCta.href}
            data-testid="hero-cta-mission"
            className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-800 font-semibold px-7 py-3.5 rounded-full border border-slate-300 transition-all duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
          >
            <Compass size={17} className="text-teal-600" />
            {site.hero.secondaryCta.label}
          </a>
        </motion.div>

        <motion.div
          ref={frameRef}
          initial={{ opacity: 0, y: 60, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-16 sm:mt-20"
        >
          <div
            aria-hidden="true"
            className="absolute -top-16 left-1/4 w-[420px] h-[420px] rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(13,148,136,0.22) 0%, transparent 65%)", filter: "blur(30px)" }}
          />
          <div
            data-testid="hero-visual-frame"
            className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-slate-200 shadow-[0_24px_80px_-24px_rgba(15,23,42,0.35)] aspect-[4/3] sm:aspect-[16/7] bg-slate-900"
          >
            <motion.img
              src={site.hero.image}
              alt={site.hero.imageAlt}
              style={{ y: imgY }}
              className="absolute inset-0 w-full h-[124%] object-cover scale-105"
              loading="eager"
            />
            <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-slate-950/10 to-transparent" />
            {site.hero.chips.map((chip, i) => (
              <span
                key={chip}
                className="chip-float absolute font-mono text-[11px] uppercase tracking-widest text-teal-100 bg-slate-950/55 backdrop-blur-sm border border-teal-300/25 px-3 py-1.5 rounded-full"
                style={{
                  left: `${8 + i * 24}%`,
                  top: `${i % 2 === 0 ? 14 : 62}%`,
                  animationDelay: `${i * 0.9}s`,
                }}
              >
                {chip}
              </span>
            ))}
            <p className="absolute bottom-4 left-5 sm:bottom-6 sm:left-8 font-mono text-[11px] sm:text-xs text-slate-300/90 tracking-wide">
              fig. 01 — an agentic loop: perceive, plan, execute, remember
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
